import React from 'react';
import { Container, Row, Col, Card, Breadcrumb, Badge, ListGroup } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { categories } from '../data/categories';

// Fix for Leaflet marker icons
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ListingPage = () => {
  const { categoryId, subcategoryId, listingId } = useParams();
  const category = categories.find(cat => cat.id === categoryId);
  const subcategory = category?.subcategories.find(sub => sub.id === subcategoryId);
  const listing = subcategory?.items.find(item => item.id === listingId);

  if (!category || !subcategory || !listing) {
    return (
      <Container className="py-5">
        <h1>Listing Not Found</h1>
        <p>The listing you're looking for doesn't exist.</p>
        <Link to="/">Return to Home</Link>
      </Container>
    );
  }

  // Generate random coordinates for Jamaica for demo purposes
  const getRandomJamaicaCoordinates = () => {
    return [
      18.1096 + (Math.random() - 0.5) * 0.2,
      -77.2975 + (Math.random() - 0.5) * 0.2
    ];
  };

  // Mock data for the listing details
  const listingDetails = {
    description: `${listing.name} is one of the most popular ${subcategory.title.toLowerCase()} in ${listing.location}, Jamaica. Known for its exceptional quality and service, it has become a favorite destination for both locals and tourists.`,
    address: `123 Main Street, ${listing.location}, Jamaica`,
    phone: '+1 (876) 555-' + Math.floor(1000 + Math.random() * 9000),
    email: `info@${listing.id.replace(/-/g, '')}.com`,
    website: `https://www.${listing.id.replace(/-/g, '')}.com`,
    hours: [
      'Monday: 9:00 AM - 5:00 PM',
      'Tuesday: 9:00 AM - 5:00 PM',
      'Wednesday: 9:00 AM - 5:00 PM',
      'Thursday: 9:00 AM - 5:00 PM',
      'Friday: 9:00 AM - 8:00 PM',
      'Saturday: 10:00 AM - 6:00 PM',
      'Sunday: Closed'
    ],
    features: [
      'Feature 1',
      'Feature 2',
      'Feature 3',
      'Feature 4',
      'Feature 5'
    ],
    reviews: [
      {
        author: 'John D.',
        rating: 5,
        comment: 'Absolutely amazing experience! Would highly recommend to anyone visiting Jamaica.'
      },
      {
        author: 'Sarah M.',
        rating: 4,
        comment: 'Great service and beautiful location. Will definitely return on my next trip.'
      },
      {
        author: 'Michael T.',
        rating: 5,
        comment: 'One of the best experiences I had during my stay in Jamaica. A must-visit!'
      }
    ]
  };

  const coordinates = getRandomJamaicaCoordinates();

  return (
    <Container className="py-5">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>Home</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/category/${categoryId}` }}>{category.title}</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/category/${categoryId}/${subcategoryId}` }}>{subcategory.title}</Breadcrumb.Item>
        <Breadcrumb.Item active>{listing.name}</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-4">
        <Col>
          <h1>{listing.name}</h1>
          <div className="d-flex align-items-center mb-3">
            <Badge bg="success" className="me-2">Rating: {listing.rating}/5</Badge>
            <span className="text-muted">{listing.location}, Jamaica</span>
          </div>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col md={8}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>About</Card.Title>
              <Card.Text>{listingDetails.description}</Card.Text>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Contact Information</Card.Title>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>Address:</strong> {listingDetails.address}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Phone:</strong> {listingDetails.phone}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Email:</strong> {listingDetails.email}
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Website:</strong> <a href={listingDetails.website} target="_blank" rel="noopener noreferrer">{listingDetails.website}</a>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Hours of Operation</Card.Title>
              <ListGroup variant="flush">
                {listingDetails.hours.map((hour, index) => (
                  <ListGroup.Item key={index}>{hour}</ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title>Reviews</Card.Title>
              {listingDetails.reviews.map((review, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{review.author}</h6>
                      <Badge bg="primary">{review.rating}/5</Badge>
                    </div>
                    <Card.Text>{review.comment}</Card.Text>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4">
            <Card.Body>
              <Card.Title>Features</Card.Title>
              <ListGroup variant="flush">
                {listingDetails.features.map((feature, index) => (
                  <ListGroup.Item key={index}>{feature}</ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <Card.Title>Location</Card.Title>
              <div style={{ height: '300px', width: '100%', borderRadius: '10px', overflow: 'hidden' }}>
                <MapContainer 
                  center={coordinates} 
                  zoom={13} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={coordinates}>
                    <Popup>
                      <strong>{listing.name}</strong><br />
                      {listingDetails.address}
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <h3 className="mb-4">Other {subcategory.title} You Might Like</h3>
          <Row>
            {subcategory.items
              .filter(item => item.id !== listingId)
              .slice(0, 3)
              .map(item => (
                <Col key={item.id} md={4} className="mb-4">
                  <Card className="h-100 shadow-sm hover-card">
                    <Card.Body>
                      <Card.Title>{item.name}</Card.Title>
                      <Card.Subtitle className="mb-2 text-muted">{item.location}</Card.Subtitle>
                      <div className="mb-3">
                        <Badge bg="success">Rating: {item.rating}/5</Badge>
                      </div>
                      <Link 
                        to={`/listing/${categoryId}/${subcategoryId}/${item.id}`}
                        className="btn btn-outline-primary"
                      >
                        View Details
                      </Link>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            }
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default ListingPage;
